import React, { useEffect, useState, useMemo } from 'react';
import styles from './RedDayRain.module.css';

// ─── Two themes, randomly chosen each trigger ──────────────

const THEMES = {
  sadRain: {
    emojis: ['😢', '💧', '☁️', '😭', '🌧️', '💧', '😢', '☁️', '💧', '😭'],
    className: 'sadRain',
  },
  clownParade: {
    emojis: ['🤡', '🎪', '🤡', '🎈', '🤡', '🎪', '🤡', '🎈', '🤡', '🎪'],
    className: 'clownParade',
  },
};

type ThemeKey = keyof typeof THEMES;

const PARTICLE_COUNT = 55;

interface Particle {
  id: number;
  emoji: string;
  left: number;
  delay: number;
  duration: number;
  size: number;
  wobble: number;
  spinDirection: number; // 1 or -1
}

function generateParticles(emojis: string[]): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    emoji: emojis[Math.floor(Math.random() * emojis.length)],
    left: Math.random() * 100,
    delay: Math.random() * 2.5,
    duration: 2.5 + Math.random() * 2.5,
    size: 22 + Math.random() * 26,
    wobble: -30 + Math.random() * 60,
    spinDirection: Math.random() > 0.5 ? 1 : -1,
  }));
}

interface RedDayRainProps {
  /** Set to true to trigger the animation */
  active: boolean;
  /** Duration in ms before auto-hiding (default: 5500) */
  duration?: number;
}

const RedDayRain: React.FC<RedDayRainProps> = ({ active, duration = 5500 }) => {
  const [visible, setVisible] = useState(false);

  // Pick a random theme when the component first decides to show
  const [themeKey] = useState<ThemeKey>(() => (Math.random() > 0.5 ? 'sadRain' : 'clownParade'));

  const theme = THEMES[themeKey];
  const particles = useMemo(() => generateParticles(theme.emojis), [theme.emojis]);

  useEffect(() => {
    if (active) {
      setVisible(true);
      const timer = setTimeout(() => setVisible(false), duration);
      return () => clearTimeout(timer);
    }
  }, [active, duration]);

  if (!visible) return null;

  return (
    <div className={`${styles.container} ${styles[theme.className]}`} aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.id}
          className={styles.particle}
          style={
            {
              left: `${p.left}%`,
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              fontSize: `${p.size}px`,
              '--wobble': `${p.wobble}px`,
              '--spin': `${p.spinDirection * 360}deg`,
            } as React.CSSProperties
          }
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
};

export default RedDayRain;
